import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { VendorPersonType, VendorType } from '@econtractor/types';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import ESpinner from 'components/others/Spinner';
import SolarIcon from 'components/icons/SolarIcon';
import SvgTrash from 'components/svg/SvgTrash';
import OffVendorPersonContacts from './OffVendorPersonContacts';
import VendorPersonForm from './VendorPersonForm';
import VendorPersonsTable from './VendorPersonsTable';
import useOffVendorPersons from './model/useOffVendorPersons';

interface OffVendorPersonsProps {
    vendorId: number | null;
    vendor?: VendorType;
    onHide: () => void;
    onPrimaryPersonChange?: (person: VendorPersonType | null) => void;
}

const OffVendorPersons = ({ vendorId, vendor, onHide, onPrimaryPersonChange }: OffVendorPersonsProps) => {
    const { t } = useTranslation(['Vendors', 'common']);
    const { showDlg } = useModal();
    const {
        show,
        list,
        showSpinner,
        saving,
        form,
        showForm,
        contactsPerson,
        title,
        handleClose,
        handleAdd,
        handleEdit,
        handleSubmit,
        resetForm,
        changeForm,
        openContacts,
        hideContacts,
        removePerson,
        setPrimary,
    } = useOffVendorPersons({ vendorId, onHide, onPrimaryPersonChange });

    const handleDelete = (row: VendorPersonType) => {
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1].handleClick = () => {
            showDlg(false);
            void removePerson(row);
        };

        showDlg({
            type: 'confirm',
            buttons,
            text: (
                <>
                    <h4 className="d-flex pb-2 mb-2 border-bottom text-danger">
                        <SvgTrash className="text-danger" />
                        <span className="ps-2">{t('persons.confirmDelete')}</span>
                    </h4>
                    <h5 className="mb-0">{row.fullName}</h5>
                    {row.position ? <small className="form-text text-muted">{row.position}</small> : null}
                </>
            ),
        });
    };

    return (
        <>
            <Offcanvas show={show} onHide={handleClose} placement="end" className="off-vendor-persons">
                <Offcanvas.Header className="border-bottom" closeButton>
                    <Offcanvas.Title className="me-auto">
                        {title}
                        {vendor?.vendorName ? (
                            <span className="text-muted fs-6 d-block fw-normal">{vendor.vendorName}</span>
                        ) : null}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-coming-form d-flex flex-column h-100">
                        {list == null ? (
                            <div className="off-coming-content px-3 pb-3">
                                <ESpinner />
                            </div>
                        ) : showForm ? (
                            <VendorPersonForm
                                form={form}
                                saving={saving}
                                onChange={changeForm}
                                onCancel={resetForm}
                                onSubmit={() => void handleSubmit()}
                            />
                        ) : (
                            <div className="off-coming-content px-3 pb-3">
                                <VendorPersonsTable
                                    rows={list}
                                    showSpinner={showSpinner}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    onOpenContacts={openContacts}
                                    onSetPrimary={setPrimary}
                                />
                                <div className="d-flex justify-content-center mt-3">
                                    <Button
                                        type="button"
                                        variant="info"
                                        size="sm"
                                        className="me-2"
                                        onClick={handleAdd}
                                    >
                                        <SolarIcon name="plus" className="me-1" />
                                        {t('persons.toolAdd')}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            {vendorId ? (
                <OffVendorPersonContacts vendorId={vendorId} person={contactsPerson} onHide={hideContacts} />
            ) : null}
        </>
    );
};

export default OffVendorPersons;
